import { forwardRef, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useCardProvider } from "Payments/globalPaymentsStateHooks";
import { AnimatePresence, motion } from "framer-motion";
import luhn from "luhn";

import LoadingBricks from "UI/FnrLoadingBricks2";

import StaxCardNumberInput from "./StaxCardNumberInput";
import cardTypes from "./cardTypes";
import useCreditCardEntryState from "./state";
import {
  InputOutline,
  InputWrapper,
  NumberInput,
  iPhone,
  iPhoneMini,
  useErrorShakeAnimation,
} from "./styles";

const CardNumberInput = forwardRef(
  ({ expirationInputRef, submitting, paymentMethodLast4 }, inputRef) => {
    const { providerIsStax } = useCardProvider();

    const [isHovering, setIsHovering] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    //this is used to hide the camera button since it's not done yet
    const [canShowCameraButton] = useState(false);

    const [
      {
        ocr: { show: showOCR, ccNumber: ccNumberOCR },
        ccNumber,
        last4,
        canBeEdited,
        isEditing,
        // validateTrigger,
      },
      dispatch,
    ] = useCreditCardEntryState();

    const shouldShowLast4Only = canBeEdited && !isEditing;

    const [cursor, setCursor] = useState(null);

    const setCcNumber = (number) => {
      dispatch({ type: "Set Credit Card Number", number });
    };

    const numberOfSpaces = useRef(0);

    const errorShakeAnimation = useErrorShakeAnimation();

    const openOCR = () => dispatch({ type: "Show OCR" });

    useEffect(() => {
      if (ccNumberOCR?.length >= 15) {
        let {
          number: newCcNumber,
          cardType,
          pureNumber,
        } = formatCreditCardNumber(ccNumberOCR, false, false);

        setAndValidateCcNumber(newCcNumber, cardType, pureNumber);
      }
    }, [ccNumberOCR]);

    const formatCreditCardNumber = (
      number,
      removeDigitAt = false,
      deleting = false
    ) => {
      number = number.replace(/\D/g, "");

      let pureNumber = (" " + number).trim();

      if (removeDigitAt) {
        number =
          number.substr(0, removeDigitAt - 1) + number.substr(removeDigitAt);
      }

      dispatch({
        type: "Remove Multiple Error Messages",
        messages: [
          "invalid_card_number",
          "invalid_card_type",
          "empty_card_number",
        ],
      });

      let cardType;
      if (number.length > 0) {
        cardType = cardTypes.find(
          ({ firstDigit }) => firstDigit === number.substr(0, 1) * 1
        );

        if (cardType) {
          dispatch({ type: "Set Card Type", cardType });

          if (number.length > cardType.cardNumberLength)
            errorShakeAnimation.shake();

          number = number.substr(0, cardType.cardNumberLength);
          pureNumber = pureNumber.substr(0, cardType.cardNumberLength);

          if (cardType.name === "American Express") {
            if (pureNumber.length === 4) {
              number = number + " ";
            } else if (number.length > 4) {
              if (number.length <= 10) {
                number = number.replace(/(.{4})(.{0,6})/g, "$1 $2");
                if (pureNumber.length === 10) number = number + " ";
              } else {
                number = number.replace(/(.{4})(.{0,6})(.{0,5})/g, "$1 $2 $3");
              }
            }
          } else {
            number = number.replace(/(.{4})/g, "$1 ");
          }

          if (pureNumber.length >= cardType.cardNumberLength) {
            number = number.trim();
          }
        } else {
          number = number.substr(0, 16);
          pureNumber = pureNumber.substr(0, 16);

          dispatch({ type: "Remove Card Type" });
          dispatch({
            type: "Remove Error Message",
            message: "invalid_card_type",
          });
          if (!deleting) {
            errorShakeAnimation.shake();
            dispatch({
              type: "Set Error Message",
              message: "invalid_card_type",
            });
          }
        }
      } else {
        dispatch({ type: "Remove Card Type" });
        dispatch({
          type: "Remove Error Message",
          message: "invalid_card_type",
        });
      }

      return { number, cardType, pureNumber };
    };

    const updateCcNumber = (e) => {
      let {
        number: newCcNumber,
        cardType,
        pureNumber,
      } = formatCreditCardNumber(
        e.target.value,
        false,
        e.nativeEvent.inputType === "deleteContentBackward" ||
          e.nativeEvent.inputType === "deleteContentForward"
      );

      const spacesBeforeCursor = (
        newCcNumber.substr(0, e.target.selectionStart).match(/ /g) || []
      ).length;
      const spacesAfterCursor = (
        newCcNumber.substr(e.target.selectionStart - 1).match(/ /g) || []
      ).length;

      if (ccNumber === newCcNumber) {
        if (e.nativeEvent.inputType === "deleteContentBackward") {
          newCcNumber = formatCreditCardNumber(
            e.target.value,
            e.target.selectionStart - spacesBeforeCursor
          ).number;
          setCursor(e.target.selectionStart - 1);
        } else if (
          e.nativeEvent.inputType === "deleteContentForward" &&
          ccNumber === newCcNumber
        ) {
          newCcNumber = formatCreditCardNumber(
            e.target.value,
            e.target.selectionStart - spacesBeforeCursor + 1
          ).number;
          setCursor(e.target.selectionStart + 1);
        }
      } else {
        let selectionStart = e.target.selectionStart;

        if (
          e.nativeEvent.inputType !== "deleteContentBackward" &&
          e.nativeEvent.inputType !== "deleteContentForward"
        ) {
          if (
            newCcNumber.substr(e.target.selectionStart - 1)?.substr(0, 1) ===
              " " &&
            newCcNumber.substr(-1) !== " "
          )
            selectionStart = selectionStart + 1;
          else if (
            newCcNumber.substr(e.target.selectionStart - 1)?.substr(0, 1) !==
              " " &&
            newCcNumber.substr(e.target.selectionStart - 2)?.substr(0, 1) ===
              " " &&
            newCcNumber.substr(-1) === " "
          ) {
            selectionStart = selectionStart - 1;
          }
        }

        setCursor(selectionStart);
      }
      setAndValidateCcNumber(newCcNumber, cardType, pureNumber);
    };

    const setAndValidateCcNumber = (newCcNumber, cardType, pureNumber) => {
      setCcNumber(newCcNumber);

      if (cardType && pureNumber.length === cardType.cardNumberLength) {
        if (!luhn.validate(pureNumber)) {
          errorShakeAnimation.shake();
          dispatch({
            type: "Set Error Message",
            message: "invalid_card_number",
          });
        } else {
          setIsHovering(false);
          expirationInputRef.current.focus();
          dispatch({
            type: "Remove Error Message",
            message: "invalid_card_number",
          });
        }
      }
    };

    // useEffect(() => {
    //   if (validateTrigger > 0) {
    //   }
    // }, [validateTrigger]);

    useEffect(() => {
      if (!ccNumber) return;

      const input = inputRef.current;
      const spaces = (ccNumber.match(/ /g) || []).length;

      let cursorPosition = cursor;

      if (spaces > numberOfSpaces.current) {
        cursorPosition++;
      }

      numberOfSpaces.current = spaces;

      if (input) input.setSelectionRange(cursorPosition, cursorPosition);
    }, [inputRef, cursor, ccNumber]);

    const handleEditButtonClick = () => {
      if (submitting) return;
      dispatch({
        type: "Set Editing",
        payload: true,
      });
      dispatch({
        type: "Set Should Retokenize",
        payload: true,
      });
    };

    const showWePayCardInput =
      !shouldShowLast4Only && !providerIsStax; /* && providerIsWePay */
    const showStaxCardInput = !shouldShowLast4Only && providerIsStax;

    return (
      <InputWrapper
        data-cy="card-number-input-wrapper"
        setIsHovering={setIsHovering}
        errorShake={errorShakeAnimation}
      >
        <InputOutline
          isFocused={!shouldShowLast4Only && !submitting && isFocused}
          isHovering={isHovering && !submitting}
          isEmpty={!shouldShowLast4Only && ccNumber.length === 0}
          placeholder="Card Number"
        />

        <AnimatePresence>
          {shouldShowLast4Only && (
            <EditButtonWrapper onClick={handleEditButtonClick}>
              <Last4>
                &bull; &bull; &bull; &bull; {paymentMethodLast4 || last4}
              </Last4>
            </EditButtonWrapper>
          )}

          {shouldShowLast4Only && !submitting && (
            <EditButton
              key="edit_button"
              whileHover={{ scale: 1.1, color: "#278dc1" }}
              whileTap={{ scale: 0.95 }}
              exit={{
                opacity: 0,
                scale: 0.6,
                transition: { type: "tween" },
              }}
              onClick={handleEditButtonClick}
            >
              <div className="fas fa-pencil-alt" />
            </EditButton>
          )}

          {showWePayCardInput && (
            <NumberInput
              ref={inputRef}
              value={ccNumber}
              type="text"
              inputMode="numeric"
              name="cardnumber"
              autoComplete="cc-number"
              autoFocus={true}
              tabIndex={1}
              onChange={updateCcNumber}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={submitting}
              data-cy="fund-it-card-number-input"
            />
          )}

          {showStaxCardInput && <StaxCardNumberInput ref={inputRef} />}

          {/* {canShowCameraButton && ccNumber.length === 0 && (
            <CameraButtonWrapper
              key="camera_wrapper"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{
                opacity: 0,
                scale: 0.8,
                transition: { type: "tween" },
              }}
            >
              <LoadingBricks
                color="#FFFFFF"
                loading={showOCR}
                wrapperInsetPixels={4}
              >
                <CameraButton
                  className="fas fa-camera-alt"
                  whileTap={{ scale: 0.9 }}
                  animate={{ x: isFocused ? 0 : -2 }}
                  onClick={openOCR}
                />
              </LoadingBricks>
            </CameraButtonWrapper>
          )} */}
        </AnimatePresence>
      </InputWrapper>
    );
  }
);

const CameraButton = styled(motion.div)`
  font-size: 28px;
  display: block;
  cursor: pointer;
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    font-size: 24px;
  }
`;
const CameraButtonWrapper = styled(motion.div)`
  position: absolute;
  right: 12px;
  bottom: 11px;
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    bottom: 10px;
  }
`;

CardNumberInput.displayName = "CardNumberInput";
export default CardNumberInput;

const EditButtonWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
`;

const EditButton = styled(motion.div)`
  background: #fff;
  color: #586b74;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 40px;
  font-size: 22px;
  position: absolute;
  right: 16px;
  top: 50%;
  margin-top: -26px;
  box-shadow: rgba(0, 0, 0, 0.4) 1px 1px 2px;
  cursor: pointer;
`;

export const Last4 = styled.div`
  appearance: none;
  outline: none;
  border: none;
  padding: 0;
  font-family: Montserrat;
  display: block;
  z-index: 500;
  background: none;
  color: #fff;
  font-size: 26px;
  height: 32px;
  font-variant-numeric: tabular-nums;
  text-shadow: rgba(0, 0, 0, 0.5) 1px 1px 2px;

  @media screen and (max-width: ${iPhone.cssWidth}) {
    font-size: 22px;
    height: 26px;
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    font-size: 20px;
    height: 24px;
  }
`;
